import { Icons } from '@/components/icons';
import {
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/primitives/Popover';
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetRoot,
  SheetTitle,
  SheetTrigger,
} from '@/components/primitives/Sheet';
import { Typography } from '@/components/primitives/Typography';
import { TransText } from '@/i18n/trans/text';
import type { ServiceAttributeDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { type FC, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ServiceTextButtonProps {
  serviceTexts: Array<ServiceAttributeDTO>;
}

const ServiceTextButton: FC<ServiceTextButtonProps> = ({ serviceTexts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  if (!serviceTexts.length) {
    return null;
  }

  const title =
    serviceTexts.length === 1 ? (
      serviceTexts[0].shortDescription
    ) : (
      <TransText i18nKey="importantInformation" />
    );

  const renderTrigger = () => {
    const ChevronComponent = isOpen ? Icons.chevronUp : Icons.chevronDown;

    return (
      <div className="grid grid-cols-[1fr_auto_1fr] gap-1 rounded border border-warning-border bg-warning-surface px-1.5 py-1 text-dark">
        <Icons.infoCircle height={16} width={16} />
        <Typography variant="body2" className="truncate">
          {title}
        </Typography>
        <ChevronComponent height={16} width={16} />
      </div>
    );
  };

  const renderDescriptions = () => (
    <div className="flex flex-col gap-3">
      {serviceTexts.map(({ description }, key) => (
        <Typography key={key} variant="paragraph">
          {description}
        </Typography>
      ))}
    </div>
  );

  if (isLaptopOrBigger) {
    return (
      <PopoverRoot open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger className="self-end">{renderTrigger()}</PopoverTrigger>
        <PopoverContent className="flex w-[360px] flex-col gap-4 p-4 text-dark">
          <div className="flex gap-3">
            <Icons.infoCircle height={20} width={20} />
            <Typography variant="subtitle">{title}</Typography>
          </div>
          {renderDescriptions()}
        </PopoverContent>
      </PopoverRoot>
    );
  }

  return (
    <SheetRoot open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger className="self-start">{renderTrigger()}</SheetTrigger>
      <SheetContent side="bottom">
        <SheetHeader showClose>
          <SheetTitle className="flex items-center gap-3">
            <Icons.infoCircle height={20} width={20} />
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">{title}</SheetDescription>
        </SheetHeader>
        <div className="p-4 pt-0">{renderDescriptions()}</div>
      </SheetContent>
    </SheetRoot>
  );
};

export default ServiceTextButton;
